import { Tool } from '../tools/Tool';
import { type EnhancedToolResult, type ToolDefinition, type ToolExecutionContext } from '../types';

import { EditHtmlAssetTool } from './EditHtmlAssetTool';
import { applyHtmlAndLint, resolveActiveHtmlAsset } from './htmlAssetToolHelpers';

export interface WriteHtmlAssetArgs {
    html: string;
}

/**
 * Sets the composition HTML on the HtmlAsset that the current design session is bound to (tracked
 * in the htmlDesign slice). The target asset is read from Redux at execution time rather than baked
 * into the tool, because tools are constructed during the agent's base constructor before any
 * session id exists. After applying, the asset re-lints the composition; this tool surfaces those
 * findings so the agent can self-correct.
 */
export class WriteHtmlAssetTool extends Tool<WriteHtmlAssetArgs> {
    public static readonly toolName = 'write_html_asset';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: WriteHtmlAssetTool.toolName,
                description:
                    'Replace the entire composition with a new self-contained HTML document for ' +
                    'the dynamic graphic. Use this to author the first version or when restructuring ' +
                    'so much that most of the markup changes; for a smaller change to an existing ' +
                    `design, prefer ${EditHtmlAssetTool.toolName} (far fewer tokens, less likely to drop unrelated ` +
                    'markup). The document must register a paused, seekable GSAP ' +
                    'timeline at window.__timelines.main, animate only via GSAP (no CSS ' +
                    'animations/transitions), be deterministic (no Date.now/Math.random), and ' +
                    'reference template colors via var(--l5-color-N). Returns lint findings; fix ' +
                    'any errors and call again before replying to the user.',
                parameters: {
                    type: 'object',
                    properties: {
                        html: {
                            type: 'string',
                            description:
                                'Complete <!DOCTYPE html>...</html> document with an inline, paused ' +
                                'GSAP timeline registered at window.__timelines.main.',
                        },
                    },
                    required: ['html'],
                },
            },
        };
    }

    public getToolDisplayName(): string {
        return 'Write design';
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(
        args: WriteHtmlAssetArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = resolveActiveHtmlAsset(
            this.getState(),
            this.luminary,
            context.conversationId,
        );
        if ('error' in resolved) {
            return resolved.error;
        }
        return applyHtmlAndLint(
            this.luminary,
            resolved.asset,
            args.html,
            resolved.scene.duration,
            context.conversationId,
        );
    }
}
